/*!

=========================================================
* Argon Dashboard React - v1.2.3
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// reactstrap components
import {
  Button,
  Card,
  CardBody,
  FormGroup,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Col,
} from 'reactstrap';

import { useState, useContext, useEffect } from 'react';
import { GlobalContext } from '../api/GlobalContext';
import { useNavigate, useLocation } from 'react-router-dom';

// The token is in the link's fragment (older emails used the query string).
function readResetLink(location) {
  const params = new URLSearchParams(
    location.hash.replace(/^#/, '') || location.search,
  );
  return { token: params.get('token'), expiry: params.get('expiry') };
}

const PasswordReset = () => {
  // Get the API
  const { apiClient } = useContext(GlobalContext);

  // Get the navigation function
  const navigate = useNavigate();

  // State variable to store the label text
  const [labelText, setLabelText] = useState('Reset Password');

  // State variables to store the form input values
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [disableResetPassword, setDisableResetPassword] = useState(false);
  const [disableIsResettingPassword, setIsResettingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();
  const [{ token, expiry: expiryTime }, setResetLink] = useState(() =>
    readResetLink(location),
  );

  // Take the token out of the address bar and history once it's read. Also
  // runs when a newer link is pasted into this tab: only the fragment changes,
  // so the page doesn't reload and would otherwise keep the old token.
  useEffect(() => {
    if (!location.hash && !location.search) return;
    const link = readResetLink(location);
    if (link.token) {
      setResetLink(link);
      setLabelText('Reset Password');
    }
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, location.search]);

  useEffect(() => {
    if (token && token.length !== 32) {
      setLabelText(
        'Invalid Link. To reset your password, return to the login page.',
      );
    }

    // Convert expiry time to milliseconds
    const expiryTimeMilliseconds = parseInt(expiryTime) * 1000;

    // Create a Date object for expiry time
    const expiryDate = new Date(expiryTimeMilliseconds);

    // Get current time
    const now = new Date();

    // Compare expiry time with current time
    if (now > expiryDate) {
      // Throw an error or handle expiry logic here
      setLabelText(
        'Link Expired. To reset your password, return to the login page.',
      );
    }
  }, [token, expiryTime]);

  // Function to handle form submission
  function handleSubmit(e) {
    e.preventDefault();
    setDisableResetPassword(true);

    // Tell the user we got their request
    setLabelText('Resetting password...');
    //password validation
    if (newPassword !== confirmPassword) {
      setLabelText('Passwords do not match');
      setDisableResetPassword(false);
      return;
    }
    if (newPassword.length < 8 || confirmPassword.length < 8) {
      setLabelText('Password too short, needs to be greater than 7 characters');
      setDisableResetPassword(false);
      return;
    }
    // Call the login function with the form values
    apiClient
      .resetPassword(confirmPassword, token)
      .then((data) => {
        // If there is an error, display it in the Sign In Area
        if ('error' in data) {
          // Send the error to the small
          setLabelText(data['error']);
        } else {
          setLabelText(
            'Your password has been updated successfully. You will be directed to login in 5 seconds.',
          );
          setTimeout(() => {
            navigate('/auth/login');
          }, 5000); // Simulate a delay of 5 second
          setIsResettingPassword(true);
        }
        setDisableResetPassword(false);
      })
      .catch((e) => {
        setDisableResetPassword('Error: ' + e);
        setDisableResetPassword(false);
      });
  }

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
      <Col lg="5" md="7">
        <Card className="bg-secondary shadow border-0">
          <CardBody className="px-lg-5 py-lg-5">
            <div className="text-center text-muted mb-4">
              <small>{labelText}</small>
            </div>
            <Form role="form" onSubmit={handleSubmit}>
              <FormGroup>
                <InputGroup className="input-group-alternative">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="ni ni-lock-circle-open" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    placeholder="New Password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </InputGroup>
              </FormGroup>
              <FormGroup>
                <InputGroup className="input-group-alternative">
                  <InputGroupAddon addonType="prepend">
                    <InputGroupText>
                      <i className="ni ni-lock-circle-open" />
                    </InputGroupText>
                  </InputGroupAddon>
                  <Input
                    placeholder="Confirm Password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </InputGroup>
              </FormGroup>
              <div className="custom-control custom-checkbox mb-3">
                <input
                  className="custom-control-input"
                  id="showPasswordCheckbox"
                  type="checkbox"
                  checked={showPassword}
                  onChange={toggleShowPassword}
                />
                <label
                  className="custom-control-label"
                  htmlFor="showPasswordCheckbox"
                >
                  Show Password
                </label>
              </div>
              <div className="text-center">
                <Button
                  className="my-4"
                  color="primary"
                  type="submit"
                  disabled={disableResetPassword || disableIsResettingPassword}
                >
                  Reset Password
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </>
  );
};

export default PasswordReset;
